import { zodResolver } from '@hookform/resolvers/zod';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@optools/components/ui/form';
import { Input } from '@optools/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@optools/components/ui/select';
import { PipelineInputConfigCategory } from '@optools/services/operator-gateway/pipeline-input-config/types';
import { PipelineInputConfigInfo } from '@optools/services/operator-gateway/types/generated/graphql';
import { ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import * as z from 'zod';

type FormKey = Extract<keyof PipelineInputConfigInfo, 'inputLabel' | 'category'>;

const formSchema = z.object({
  category: z.enum(PipelineInputConfigCategory),
  inputLabel: z.string().min(1, { error: 'Input Label is required' }),
});

export type PipelineInputConfigFormValues = z.infer<typeof formSchema>;

export interface PipelineInputConfigFormProps {
  children: ReactNode;
  config: PipelineInputConfigInfo;
  onSubmit: (values: PipelineInputConfigFormValues) => void;
}

export function PipelineInputConfigForm({
  children,
  config,
  onSubmit,
}: PipelineInputConfigFormProps) {
  const { category = '', inputLabel = '' } = config;
  const defaultValues = {
    category: category as PipelineInputConfigCategory,
    inputLabel: inputLabel as string,
  } satisfies { [key in FormKey]: PipelineInputConfigInfo[FormKey] };

  const form = useForm<PipelineInputConfigFormValues>({
    defaultValues,
    resolver: zodResolver(formSchema),
  });

  const handleSubmit = (values: PipelineInputConfigFormValues) => {
    onSubmit(values);
  };

  return (
    <Form {...form}>
      <form
        aria-label="edit-input-config"
        className="mt-2"
        onSubmit={form.handleSubmit(handleSubmit)}>
        <div className="space-y-4">
          <FormField
            control={form.control}
            name="inputLabel"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Input Label</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="category"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Category</FormLabel>
                <Select defaultValue={field.value} onValueChange={field.onChange}>
                  <FormControl>
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {Object.keys(PipelineInputConfigCategory).map((category) => (
                      <SelectItem key={category} value={category}>
                        {category}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        {children}
      </form>
    </Form>
  );
}
